import { formatTime } from "shared/utils";
import OtpInput from "shared/components/forms/inputs/OtpInput";
import { useEffect, useState } from "react";
import "styles/flows/forex/passwordManager/securityAuthStep.css";
import { useUserDetailsStore } from "shared/stores/userDetailsStore";
import { useDeviceStore } from "shared/stores/deviceStore";
import Loader from "shared/components/common/Loader";
import { useTranslation } from "react-i18next";
import { PasswordManagerFormData } from "../types";
import { useSharedStore } from "shared/stores/sharedStore";
import { showNotification } from "shared/components/common/Notifications";

const SecurityAuthStep = ({
  formData,
  handleInputChange,
}: {
  formData: PasswordManagerFormData;
  handleInputChange: (id: string, value: unknown) => void;
}) => {
  const { t } = useTranslation();
  const userDetails = useUserDetailsStore((state) => state.userDetails);
  const domain = useSharedStore((state) => state.serverDomain);
  const isMobile = useDeviceStore((state) => state.isMobile);

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(formData.activeTab);
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [timeLeft, setTimeLeft] = useState(formData.timeLeft || 0);
  const [timeLeftSms, setTimeLeftSms] = useState(formData.timeLeftSms || 0);
  const [isOtpSent, setIsOtpSent] = useState(formData.isOtpSent);

  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;
    let timerSmsId: NodeJS.Timeout | null = null;

    if (activeTab === "sms") {
      if (timeLeftSms > 0) {
        timerSmsId = setInterval(() => {
          setTimeLeftSms((prev) => {
            if (prev <= 1) {
              if (timerSmsId) clearInterval(timerSmsId);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } else {
      if (timeLeft > 0) {
        timerId = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              if (timerId) clearInterval(timerId);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }

    return () => {
      if (timerSmsId) {
        clearInterval(timerSmsId);
        handleInputChange("timeLeftSms", timeLeftSms);
      }
      if (timerId) {
        clearInterval(timerId);
        handleInputChange("timeLeft", timeLeft);
      }
    };
  }, [timeLeft, timeLeftSms, activeTab, handleInputChange]);

  const handleStartTimer = (activeTab: "sms" | "email") => {
    const otpTimer = 5;

    if (activeTab === "sms") {
      setTimeLeftSms(otpTimer);
      formData.timeLeftSms = 5;
    } else {
      setTimeLeft(otpTimer);
      formData.timeLeft = 5;
    }
  };

  // main account or meta real account
  const handleSendOtp = async () => {
    try {
      setIsLoading(true);
      const accountSelected = formData.account.value;

      let SelectAccountDropDown = "SELECT AN ACCOUNT";
      let FormLogicHidden = "sendOtpCodeMain";

      const Password = formData.password;
      const ReEnterPassword = formData.reenteredPassword;
      const MarketingInfoHidden = `isAppsInstall=false&isMobile=${isMobile}&Device=PC&ScreenRes=1920X1080&timeZone=-2&timeZoneName=Asia/Jerusalem&newdevice=Y2JiMmI2ZWEtZmNkMC1lZjExLWI4OWYtZWFiMGRmMTYxZDdi`;
      const CountryCodeHidden = userDetails?.CountryCode || "";
      const LcExtraData = activeTab;

      if (accountSelected) {
        SelectAccountDropDown = accountSelected;
        FormLogicHidden = "sendOtpCodeMeta";
      }

      const formDataRequest = new FormData();

      formDataRequest.append("SelectAccountDropDown", SelectAccountDropDown);
      formDataRequest.append("Password", Password);
      formDataRequest.append("ReEnterPassword", ReEnterPassword);
      formDataRequest.append("FormLogicHidden", FormLogicHidden);
      formDataRequest.append("MarketingInfoHidden", MarketingInfoHidden);
      formDataRequest.append("CountryCodeHidden", CountryCodeHidden);
      formDataRequest.append("LcRefHidden", "");
      formDataRequest.append("LcExtraData", LcExtraData);

      const formURL = `${document.referrer}SERVER/en/${domain}/Client/PartialForm?widgetName=PasswordManagerWidget&widgetLanguage=en&stage=3&isPopup=false`;
      const otpResponse = await fetch(formURL, {
        method: "POST",
        body: formDataRequest,
        credentials: "include",
      });

      if (otpResponse.ok) {
        const otpData = await otpResponse.text();
        console.log(otpData);

        switch (true) {
          case otpData === "otp_sent": {
            const newIsOtpSent = {
              ...isOtpSent,
              [activeTab]: isOtpSent[activeTab] + 1,
            };

            setIsOtpSent(newIsOtpSent);
            handleStartTimer(activeTab);

            handleInputChange("isOtpSent", newIsOtpSent);
            break;
          }
          case otpData.includes("otp_sent_failed"): {
            if (
              otpData.includes("UserBlock") ||
              otpData.includes("SmsBlock") ||
              otpData.includes("MailBlock")
            ) {
              showNotification({
                type: "error",
                title: t("PasswordManager.OTPUserBlockedTitle"),
                message: t("PasswordManager.OTPUserBlockedMessage"),
              });
              // showOtpToastr("password-manager-user-blocked");
            } else if (otpData.includes("InvalidParameter")) {
              // showOtpToastr("password-manager-user-details-wrong");
              showNotification({
                type: "error",
                title: t("PasswordManager.OTPInvalidDetailsTitle"),
                message: t("PasswordManager.OTPInvalidDetailsMessage"),
              });
            }
            break;
          }

          default:
            throw new Error("unknown otp response");
        }
      } else {
        if (!import.meta.env.PROD) {
          const newIsOtpSent = {
            ...isOtpSent,
            [activeTab]: isOtpSent[activeTab] + 1,
          };

          setIsOtpSent(newIsOtpSent);
          handleStartTimer(activeTab);

          handleInputChange("isOtpSent", newIsOtpSent);
        }
        throw new Error("otp response failed");
      }
    } catch (error) {
      showNotification({
        type: "error",
        title: t("PasswordManager.GeneralErrorTitle"),
        message: t("PasswordManager.GeneralErrorMessage"),
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = () => {
    if (!isOtpValid || !otpValue) return;

    handleInputChange("otpValue", otpValue);
  };

  const handleTabChange = (tab: "sms" | "email") => {
    setActiveTab(tab);
    handleInputChange("activeTab", tab);
  };

  return (
    <>
      <div className="security-auth-container">
        <main>
          <h2>{t("PasswordManager.SecurityAuth.Title")}</h2>
          <h3>{t("PasswordManager.SecurityAuth.SubTitle")}</h3>
          <div className="tabs-container">
            <div className="tabs-header">
              <div
                className={`tab ${activeTab === "sms" ? "active" : ""}`}
                onClick={() => handleTabChange("sms")}
              >
                {t("PasswordManager.SecurityAuth.SMS")}
              </div>
              <div
                className={`tab ${activeTab === "email" ? "active" : ""}`}
                onClick={() => handleTabChange("email")}
              >
                {t("PasswordManager.SecurityAuth.Email")}
              </div>
            </div>
            {/* Tab contents */}
            <div className="tabs-content">
              {activeTab === "sms" && (
                <>
                  <label>
                    {isOtpSent.sms
                      ? `${t(
                          "PasswordManager.SecurityAuth.VerficationTextComplete"
                        )} ${userDetails?.Phone}.`
                      : `${t(
                          "PasswordManager.SecurityAuth.VerficationText"
                        )} ${t("PasswordManager.Phone")}: ${
                          userDetails?.Phone
                        }`}
                  </label>
                  {isOtpSent.sms > 0 && (
                    <>
                      <OtpInput
                        onChange={(otpValue, isComplete) => {
                          setIsOtpValid(isComplete);
                          setOtpValue(otpValue);
                        }}
                      />

                      <div className="otp-recieve-container">
                        <span>
                          {t("PasswordManager.SecurityAuth.DidntRecieve")}
                        </span>
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={timeLeftSms > 0}
                        >
                          {t("PasswordManager.SecurityAuth.Resend")}
                        </button>
                        {timeLeftSms > 0 && (
                          <span> in {formatTime(timeLeftSms)}</span>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {activeTab === "email" && (
                <>
                  <label>
                    {isOtpSent.email
                      ? `${t(
                          "PasswordManager.SecurityAuth.VerficationTextComplete"
                        )} ${userDetails?.Email}.`
                      : `${t(
                          "PasswordManager.SecurityAuth.VerficationText"
                        )} ${t("PasswordManager.SecurityAuth.Email")}: ${
                          userDetails?.Email
                        }`}
                  </label>
                  {isOtpSent.email && (
                    <>
                      <OtpInput
                        onChange={(otpValue, isComplete) => {
                          setIsOtpValid(isComplete);
                          setOtpValue(otpValue);
                        }}
                      />

                      <div className="otp-recieve-container">
                        <span>
                          {t("PasswordManager.SecurityAuth.DidntRecieve")}
                        </span>
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={timeLeft > 0}
                        >
                          {t("PasswordManager.SecurityAuth.Resend")}
                        </button>
                        {timeLeft > 0 && (
                          <span> in {formatTime(timeLeft)}</span>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </main>

        <div className="next-submit-button">
          {(activeTab === "sms" && isOtpSent.sms > 0) ||
          (activeTab === "email" && isOtpSent.email > 0) ? (
            <button type="submit" onClick={handleApply} disabled={!isOtpValid}>
              {t("PasswordManager.SecurityAuth.Apply")}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={activeTab === "sms" ? timeLeftSms > 0 : timeLeft > 0}
            >
              {t("PasswordManager.SecurityAuth.Send")}
            </button>
          )}
        </div>
      </div>
      {isLoading && <Loader isTransparent={isLoading} />}
    </>
  );
};

export default SecurityAuthStep;
